import { useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { getLatestWorkflowExecution } from 'api/workflows/requests.ts';
import { KeyboardEvent } from 'react';
import { QueryKey } from 'api/model.ts';
import { useDrawer } from 'hooks/useDrawer.ts';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ChecklistIcon from '@mui/icons-material/Checklist';
import {
  ToggleSegment,
  ToggleContainer,
  TogglePill,
  SegmentIcon,
  SegmentLabel,
} from './WorkflowEditorToggle.styles.ts';

type Mode = 'editor' | 'executions';

const WorkflowEditorToggle = () => {
  const navigate = useNavigate();
  const { projectId, workflowId, workflowExecutionId } = useParams();
  const drawer = useDrawer();
  const mode: Mode = !workflowExecutionId ? 'editor' : 'executions';

  const {
    data: latestExecution,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: [QueryKey.WorkflowExecutions, { workflowId }],
    queryFn: () => getLatestWorkflowExecution(workflowId ?? ''),
  });

  const goToMode = async (value: Mode) => {
    const workflowURL = `/projects/${projectId}/workflows/${workflowId}/canvas`;
    if (value === 'editor') {
      drawer.close();
      void navigate(workflowURL);
    } else {
      const { data: execution } = await refetch();
      if (execution) {
        void navigate(`${workflowURL}/revision/${execution.workflow_revision}/executions/${execution.id}`);
        drawer.openExecutions();
      }
    }
  };

  const canToggle = !isFetching && (mode === 'executions' || !!latestExecution);

  const handleToggle = () => {
    if (!canToggle) return;
    void goToMode(mode === 'editor' ? 'executions' : 'editor');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggle();
    }
  };

  return (
    <ToggleContainer
      role="switch"
      aria-checked={mode === 'executions'}
      aria-label="Toggle between editor and executions"
      tabIndex={canToggle ? 0 : -1}
      disabled={!canToggle}
      onClick={handleToggle}
      onKeyDown={handleKeyDown}
    >
      <TogglePill mode={mode} />
      <ToggleSegment active={mode === 'editor'}>
        <SegmentIcon>
          <AccountTreeOutlinedIcon />
        </SegmentIcon>
        <SegmentLabel>Editor</SegmentLabel>
      </ToggleSegment>
      <ToggleSegment active={mode === 'executions'}>
        <SegmentIcon>
          <ChecklistIcon />
        </SegmentIcon>
        <SegmentLabel>Executions</SegmentLabel>
      </ToggleSegment>
    </ToggleContainer>
  );
};

export default WorkflowEditorToggle;
